import React, { FC } from 'react';
import styles from './styles.module.css';
import { ExpansionPanel } from 'Shared/components';
import { PolicyUpdatedActivity } from '@clearcover/core-app-types';
import {
  Asset,
  UserOutlineIcon,
  VehicleOutlineIcon,
  ShieldIcon,
} from '@clearcover/atlas';
import classNames from 'classnames';
import {
  toPolicyCoverageCardAndDetails,
  toVehicleCoverageCardAndDetails,
} from 'Policies/components/PolicyActivities/Details/PolicyChanges/CoverageChange';
import { toDriverCardAndDetails } from 'Policies/components/PolicyActivities/Details/PolicyChanges/DriverChange';
import { toVehicleCardAndDetails } from 'Policies/components/PolicyActivities/Details/PolicyChanges/VehicleChange';
import { toDiscountChangeCardAndDetails } from 'Policies/components/PolicyActivities/Details/PolicyChanges/DiscountChange';
import { toAddressChangeCardAndDetails } from 'Policies/components/PolicyActivities/Details/PolicyChanges/AddressChange';
import { toInsurableItemRelationshipChangeCardAndDetails } from 'Policies/components/PolicyActivities/Details/PolicyChanges/InsurableItemRelationshipChanges';
import {
  ChangeCardAndDetails,
  CoveragesAndEndorsementsDiff,
} from 'Policies/components/PolicyActivities/Details/PolicyChanges/PolicyChangeCard';

export type ChangeSummarySectionType = {
  activity: PolicyUpdatedActivity;
  className?: string;
};

const getAllChanges = (
  activity: PolicyUpdatedActivity
): Array<ChangeCardAndDetails> => {
  const {
    insurableItems,
    insurableItemParties,
    policyCoverages,
    policyEndorsements,
    vehicleCoveragesAndEndorsements,
    multiLineDiscounts,
    addresses,
    insurableItemRelationships,
  } = activity.data.changeset;

  const versionId = activity.data.versionId;

  const policyCoveragesAndEndorsements: Array<CoveragesAndEndorsementsDiff> =
    policyEndorsements.length || policyCoverages.length
      ? [
          {
            coverages: policyCoverages,
            endorsements: policyEndorsements,
          },
        ]
      : [];

  return [
    ...insurableItems.map((item) => toVehicleCardAndDetails(item, versionId)),
    ...insurableItemParties.map(toDriverCardAndDetails),
    ...policyCoveragesAndEndorsements.map((coverage) =>
      toPolicyCoverageCardAndDetails(coverage, versionId, false)
    ),
    ...vehicleCoveragesAndEndorsements.map((coverage) =>
      toVehicleCoverageCardAndDetails(coverage, versionId, false)
    ),
    ...multiLineDiscounts.map((item) => toDiscountChangeCardAndDetails(item)),
    ...addresses.map((item) => toAddressChangeCardAndDetails(item)),
    ...insurableItemRelationships.map((item) =>
      toInsurableItemRelationshipChangeCardAndDetails(item)
    ),
  ];
};

const getChangeHeaderContent = (
  change: ChangeCardAndDetails
): React.ReactElement => {
  const { type, action, title, value } = change.metadata;

  const actionColor = action.toLowerCase();

  switch (type) {
    case 'driver':
      return (
        <div className={styles.changeHeader}>
          <div>
            <Asset
              icon={UserOutlineIcon}
              description="Driver icon"
              height={24}
              width={24}
            />
          </div>
          <div className={styles.changeInfo}>
            <div className={styles.changePrimary}>{value}</div>
            <div
              className={classNames(
                styles.changeSecondary,
                styles[actionColor]
              )}
            >
              {action.toUpperCase()}
            </div>
          </div>
        </div>
      );

    case 'vehicle':
      return (
        <div className={styles.changeHeader}>
          <div className={styles.changeIcon}>
            <Asset
              icon={VehicleOutlineIcon}
              description="Vehicle icon"
              height={24}
              width={24}
            />
          </div>
          <div className={styles.changeInfo}>
            <div className={styles.changePrimary}>{value}</div>
            <div
              className={classNames(
                styles.changeSecondary,
                styles[actionColor]
              )}
            >
              {action.toUpperCase()}
            </div>
          </div>
        </div>
      );

    case 'coverage':
      return (
        <div className={styles.changeHeader}>
          <div className={styles.changeIcon}>
            <Asset
              icon={ShieldIcon}
              description="Coverage icon"
              height={24}
              width={24}
            />
          </div>
          <div className={styles.changeInfo}>
            <div className={styles.changePrimary}>{value}</div>
            <div
              className={classNames(
                styles.changeSecondary,
                styles[actionColor]
              )}
            >
              {action.toUpperCase()}
            </div>
          </div>
        </div>
      );

    default:
      return (
        <div className={styles.changeHeader}>
          <div className={styles.changeInfo}>
            <div className={styles.changePrimary}>
              {title}: {value}
            </div>
            <div
              className={classNames(
                styles.changeSecondary,
                styles[actionColor]
              )}
            >
              {action.toUpperCase()}
            </div>
          </div>
        </div>
      );
  }
};

const IndividualChangePanel: FC<{
  change: ChangeCardAndDetails;
  index: string | number;
}> = ({ change, index }) => {
  const DetailsComponent = change.details || (() => null);

  return (
    <div className={classNames(styles.changePanel)}>
      <ExpansionPanel
        id={`change-${index}`}
        head={getChangeHeaderContent(change)}
        open={false}
      >
        <div className={styles.changeDetailsContainer}>
          <DetailsComponent />
        </div>
      </ExpansionPanel>
    </div>
  );
};

const groupChangesByType = (changes: Array<ChangeCardAndDetails>) => {
  const vehicles: Array<ChangeCardAndDetails> = [];
  const drivers: Array<ChangeCardAndDetails> = [];
  const coverages: Array<ChangeCardAndDetails> = [];
  const other: Array<ChangeCardAndDetails> = [];

  changes.forEach((change) => {
    const { type } = change.metadata;

    if (type === 'vehicle') {
      vehicles.push(change);
    } else if (type === 'driver') {
      drivers.push(change);
    } else if (type === 'coverage') {
      coverages.push(change);
    } else {
      other.push(change);
    }
  });

  return { vehicles, drivers, coverages, other };
};

const ChangeGroup: FC<{
  title: string;
  changes: Array<ChangeCardAndDetails>;
  testId: string;
}> = ({ title, changes, testId }) => {
  if (changes.length === 0) {
    return null;
  }

  return (
    <div className={styles.changeGroup}>
      <div className={styles.groupHeader}>
        <span className={styles.groupTitle}>{title}</span>
      </div>
      <div className={styles.groupItems}>
        {changes.map((change, index) => (
          <IndividualChangePanel
            key={index}
            change={change}
            index={`${testId}-${index}`}
          />
        ))}
      </div>
    </div>
  );
};

export const ChangeSummarySection: FC<ChangeSummarySectionType> = ({
  activity,
  className,
}) => {
  const allChanges = getAllChanges(activity);
  const { vehicles, drivers, coverages, other } =
    groupChangesByType(allChanges);

  return (
    <div data-testid="change-summary-section" className={className}>
      <ChangeGroup title="Vehicles" changes={vehicles} testId="vehicles" />
      <ChangeGroup title="Drivers" changes={drivers} testId="drivers" />
      <ChangeGroup title="Coverages" changes={coverages} testId="coverages" />
      {other.length > 0 && (
        <ChangeGroup title="Other Changes" changes={other} testId="other" />
      )}
    </div>
  );
};
